import { Box, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import {
  CustomDonutChart,
  Typography,
  colors,
  IconGraphy,
  Avatar,
  styles,
  Tooltip,
} from '@leapeasy/ui-kit';
import { formatDashboardCount } from 'utils/formatDashboardCount';

function createData(stageNumber, stage, app, percent, background, color) {
  return {
    stageNumber,
    stage,
    app,
    percent,
    background,
    color,
  };
}

const STAGE_DRILLDOWN_MAP = {
  'Resident Evaluation': '01 - Tenant Evaluation',
  'Document Verification': '02 - Document Verification and Onboarding',
  'Final Review': '02A - Final Review',
  Payment: '04 - Agreement Signed',
  'Policy Issued': '05 - Policy Issued',
  'Policy Declined': '06 - Policy Declined',
  'Cancelled Application': 'Cancelled Application',
};

export const ApplicationStageChart = ({ data, onApplicationsDrilldown }) => {
  const {
    stage01,
    stage02,
    stage02A,
    stage04,
    stage05,
    totalCount,
    declinedCount,
    cancelledCount,
  } = data;
  const stageColors = [
    colors.parisGreen[100],
    colors.purpleIrish[100],
    colors.warmBlue[100],
    colors.blue[600],
    colors.sunglow[100],
    colors.tomato[100],
    colors.magentaRed[100],
  ];

  const stageList = [
    createData(
      1,
      'Resident Evaluation',
      stage01,
      stage01 / totalCount,
      colors.parisGreen[50],
      colors.parisGreen[200],
    ),
    createData(
      2,
      'Document Verification',
      stage02,
      stage02 / totalCount,
      colors.purpleIrish[50],
      colors.purpleIrish[200],
    ),
    createData(
      3,
      'Final Review',
      stage02A,
      stage02A / totalCount,
      colors.warmBlue[50],
      colors.warmBlue[200],
    ),
    createData(4, 'Payment', stage04, stage04 / totalCount, colors.blue[200], colors.blue[600]),
    createData(
      5,
      'Policy Issued',
      stage05,
      stage05 / totalCount,
      colors.sunglow[50],
      colors.sunglow[200],
    ),
    createData(
      6,
      'Policy Declined',
      declinedCount,
      declinedCount / totalCount,
      colors.tomato[50],
      colors.tomato[200],
    ),
    createData(
      7,
      'Cancelled Application',
      cancelledCount,
      cancelledCount / totalCount,
      colors.magentaRed[50],
      colors.magentaRed[200],
    ),
    createData(8, 'Total', totalCount, 1, '#F3F1F4', 'black'),
  ];

  return (
    <Box
      sx={{
        boxShadow: styles.boxShadow.sm,
        borderRadius: `${styles.borderRadius.large}px`,
        border: `1px solid ${colors.black[300]}`,
        width: '100%',
        padding: '16px',
        boxSizing: 'border-box',
      }}
      display="flex"
      flexDirection="column"
      gap="12px"
    >
      <Box display="flex" gap="16px">
        <Avatar size="large" iconImage={<IconGraphy icon="EditorLayout.AutoAwesomeMotion" />} />
        <Box>
          <Typography variant="h4" style={{ color: colors.purple[900], fontWeight: '400' }}>
            Application Stage
          </Typography>

          <Typography variant="body3" style={{ color: colors.neutral[900], fontWeight: '400' }}>
            Detailed breakdown of applications at each stage, including the total number and
            percentage of applications.
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          background: 'rgba(249, 248, 249, 0.8)',
          border: `1px solid ${colors.purple[100]}`,
          borderRadius: '8px',
          padding: '16px',
          width: '100%',
          boxSizing: 'border-box',
        }}
        display="flex"
        gap="20px"
      >
        <Box
          sx={{
            flex: '1 1 0%',
            background: 'white',
            border: `1px solid ${colors.black[200]}`,
            borderRadius: '4px',
            boxSizing: 'border-box',
            overflow: 'hidden',
          }}
          display="flex"
          flexDirection="column"
          gap="24px"
        >
          <Table
            sx={{
              tableLayout: 'fixed',
              '& .MuiTableCell-root': {
                borderBottom: 'none',
                padding: '4px 12px',
                verticalAlign: 'middle',
              },
              '& .MuiTableBody-root .MuiTableCell-root': {
                height: '20px',
                fontSize: '10px',
                lineHeight: '15px',
              },
              '& .MuiTableHead-root .MuiTableCell-root': {
                height: '32px',
                fontSize: '12px',
                fontWeight: 500,
                lineHeight: '19.8px',
                color: colors.purple[900],
              },
            }}
          >
            <TableHead sx={{ background: '#F9F8F9' }}>
              <TableRow>
                <TableCell sx={{ width: '24px' }}></TableCell>
                <TableCell variant="head" className="p-2">
                  Stage
                </TableCell>
                <TableCell variant="head" className="p-2">
                  #Apps
                </TableCell>
                <TableCell variant="head" className="p-2">
                  Total %
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {stageList.map((n, index) => {
                const drilldownKey = STAGE_DRILLDOWN_MAP[n.stage];
                const rowClickable =
                  !!onApplicationsDrilldown && !!drilldownKey && n.stage !== 'Total';
                return (
                  <TableRow
                    key={`${n.stage}-${index}`}
                    hover={rowClickable}
                    onClick={() => rowClickable && onApplicationsDrilldown(drilldownKey)}
                    sx={
                      rowClickable
                        ? {
                            cursor: 'pointer',
                            '&:hover': { backgroundColor: 'rgba(112, 37, 114, 0.06)' },
                          }
                        : undefined
                    }
                  >
                    <TableCell>
                      <Box
                        sx={{
                          width: '20px',
                          height: '20px',
                          borderRadius: '4px',
                          background: n.background,
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Tooltip title={n.stage} placement="top" showCarot>
                        <Box sx={{ width: 'fit-content', maxWidth: '100%' }}>
                          <Typography variant="small" style={{ fontWeight: '500' }}>
                            {n.stage}
                          </Typography>
                        </Box>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Typography variant="small" style={{ fontWeight: '500' }}>
                        {formatDashboardCount(n.app)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="small" style={{ fontWeight: '500' }}>
                        {n.stage !== 'Total' ? (n.percent * 100)?.toFixed(2) : 100}%
                      </Typography>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>

        <Box
          sx={{
            flex: '1 1 0%',
            background: 'white',
            border: `1px solid ${colors.black[200]}`,
            borderRadius: '4px',
            padding: '20px',
            boxSizing: 'border-box',
          }}
          display="flex"
          justifyContent="center"
          flexDirection="column"
          gap="20px"
        >
          <CustomDonutChart
            chartWidth={250}
            chartHeight={250}
            donutMinAngle={30}
            labelItemBackgroundColor="#F9F8F9"
            data={stageList
              .filter((stage) => stage.stage !== 'Total')
              .map((stage) => ({
                name: stage.stage,
                value: (stage.app / totalCount) * 100,
              }))}
            isPercentageValue={true}
            colors={stageColors}
            title="Total application"
            description={formatDashboardCount(totalCount)}
          />
        </Box>
      </Box>
    </Box>
  );
};
